import React from 'react';
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';

const CONTACT_EMAIL = 'contato@workflowbrasil.com';

export interface SiteContactSectionProps {
  onOpenDemo: () => void;
  onOpenDiagnosis: () => void;
}

const steps = [
  'Diagnóstico do ambiente operacional atual',
  'Mapeamento de processos críticos e gargalos',
  'Demonstração técnica customizada ao seu setor',
  'Proposta de implantação e ROI projetado',
];

export function SiteContactSection({ onOpenDemo, onOpenDiagnosis }: SiteContactSectionProps) {
  return (
    <section id="contact" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 xl:gap-20 items-center">
          <div>
            <p className="uppercase tracking-widest text-blue-400 mb-4" style={{ fontSize: '0.7rem', fontWeight: 700 }}>
              Avaliação Consultiva B2B
            </p>
            <h2
              className="text-white mb-5"
              style={{ fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' }}
            >
              Inicie uma Avaliação Técnica com Nossos Engenheiros Especialistas.
            </h2>
            <p className="text-slate-400 leading-relaxed mb-10" style={{ fontSize: '0.975rem' }}>
              Cada implantação começa com uma avaliação técnica conduzida por engenheiros especialistas
              para garantir plena aderência ao seu contexto operacional. Não há checkout público ou
              contratação automática.
            </p>

            <div className="mb-10">
              <p className="text-slate-500 uppercase tracking-widest mb-5" style={{ fontSize: '0.65rem', fontWeight: 700 }}>
                Processo de Avaliação
              </p>
              <div className="flex flex-col gap-3.5">
                {steps.map((step, i) => (
                  <div key={step} className="flex items-start gap-3.5">
                    <div className="w-6 h-6 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-blue-400" style={{ fontSize: '0.62rem', fontWeight: 800 }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-slate-400 leading-snug" style={{ fontSize: '0.875rem' }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3.5">
              {[
                { icon: Phone, label: 'Comercial', value: '+55 (11) 3000-0000' },
                { icon: Mail, label: 'E-mail corporativo', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
                { icon: MapPin, label: 'Sede', value: 'São Paulo, SP — Brasil' },
              ].map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-center gap-3.5">
                  <div className="p-2 bg-slate-800 border border-slate-700 rounded-lg shrink-0">
                    <Icon size={14} className="text-slate-400" />
                  </div>
                  <div>
                    <p className="text-slate-600" style={{ fontSize: '0.7rem', fontWeight: 500 }}>{label}</p>
                    {href ? (
                      <a href={href} className="text-slate-300 hover:text-white transition-colors" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                        {value}
                      </a>
                    ) : (
                      <p className="text-slate-300" style={{ fontSize: '0.875rem', fontWeight: 600 }}>{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-2xl" style={{ boxShadow: '0 32px 64px -12px rgba(0,0,0,0.35)' }}>
            <h3 className="mb-2 text-xl font-extrabold text-slate-900 md:text-2xl">
              Pronto para Transformar sua Operação?
            </h3>
            <p className="text-slate-600 leading-relaxed mb-6" style={{ fontSize: '0.875rem' }}>
              Mais de 340 organizações confiam no DataFlow para governança e performance operacional.
              Inicie com uma demonstração técnica sem compromisso.
            </p>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
              <p className="text-blue-800 mb-3" style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                O que está incluído
              </p>
              {[
                'Demonstração técnica personalizada ao seu setor',
                'Diagnóstico preliminar de processos sem custo',
                'Proposta com ROI projetado para o seu ambiente',
                'Acesso a trial supervisionado por engenheiro',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5 mb-2 last:mb-0">
                  <CheckCircle2 size={13} className="text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-blue-900" style={{ fontSize: '0.825rem' }}>{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2.5 mb-5">
              <button
                type="button"
                onClick={onOpenDemo}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-3.5 transition-colors shadow-sm"
                style={{ fontWeight: 600, fontSize: '0.925rem' }}
              >
                Agendar Demonstração Técnica
                <ArrowRight size={15} />
              </button>
              <button
                type="button"
                onClick={onOpenDiagnosis}
                className="w-full flex items-center justify-center text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg py-3.5 transition-all"
                style={{ fontWeight: 500, fontSize: '0.925rem' }}
              >
                Solicitar Diagnóstico Operacional
              </button>
            </div>

            <div className="flex items-start gap-2 border-t border-slate-100 pt-4">
              <span className="text-amber-500 shrink-0" style={{ fontSize: '0.75rem' }}>⚠</span>
              <p className="text-slate-500 leading-relaxed" style={{ fontSize: '0.75rem' }}>
                <strong className="text-slate-600">Nota:</strong> Não há checkout público ou contratação automática.
                Esta etapa é uma avaliação de capacidade conduzida por engenheiros especialistas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
